import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Button from 'core-system/Button'
import Modal, { ModalBody, ModalFooter, ModalHeader } from 'core-system/Modal'
import Text from 'core-system/Text'
import TextField from 'core-system/TextField'
import palette from 'core-system/Themes/palette'
import { TransitAgency } from 'redux/transitPass/transitPassTypes'

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  width: 100%;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
`

const LengthHint = styled(Text)`
  margin-top: 0.5rem;
`

const digitsOnly = (value: string) => value.replace(/\D/g, '')

export const formatAllowedDigitLengths = (lengths: number[]): string => {
  const values = lengths.map(String)
  if (values.length === 1) {
    return values[0]
  }
  if (values.length === 2) {
    return `${values[0]} or ${values[1]}`
  }
  return `${values.slice(0, -1).join(', ')}, or ${values[values.length - 1]}`
}

interface LinkTransitPassModalProps {
  open: boolean
  agency: TransitAgency
  initialCardNumber?: string | null
  allowedDigitCounts?: number[]
  onClose: () => void
  onSave: (cardNumber: string) => void
}

const LinkTransitPassModal = React.memo(
  ({
    open,
    agency,
    initialCardNumber = '',
    allowedDigitCounts = [],
    onClose,
    onSave,
  }: LinkTransitPassModalProps) => {
    const [cardNumber, setCardNumber] = useState(
      digitsOnly(initialCardNumber ?? '')
    )

    useEffect(() => {
      if (open) {
        setCardNumber(digitsOnly(initialCardNumber ?? ''))
      }
    }, [open, initialCardNumber])

    const allowedLengths = useMemo(
      () =>
        allowedDigitCounts.filter(
          (length) => Number.isInteger(length) && length > 0
        ),
      [allowedDigitCounts]
    )
    const maxAllowedLength =
      allowedLengths.length > 0 ? Math.max(...allowedLengths) : undefined
    const isAllowedLength =
      allowedLengths.length === 0 || allowedLengths.includes(cardNumber.length)
    const showLengthError = cardNumber.length > 0 && !isAllowedLength
    const canSave = Boolean(cardNumber) && isAllowedLength

    const handleChange = (value: string) => {
      const nextDigits = digitsOnly(value)
      setCardNumber(
        maxAllowedLength != null
          ? nextDigits.slice(0, maxAllowedLength)
          : nextDigits
      )
    }

    const handleSave = () => {
      if (!canSave) {
        return
      }
      onSave(cardNumber)
      onClose()
    }

    return (
      <Modal open={open} onClose={onClose} width='28rem'>
        <ModalHeader
          title={`${initialCardNumber ? 'Edit' : 'Enter'} ${agency.name} Card`}
          onClose={onClose}
        />
        <ModalBody>
          <Content>
            <Text variant='body1' textColor={palette.text.secondary}>
              {`Enter your ${agency.name} card number. We'll automatically detect the card format and route it to the correct portal.`}
            </Text>
            <FieldGroup>
              <TextField
                label='Card Number'
                placeholder={`Enter ${agency.name} card number`}
                name={`linkAgencyCardNumber-${agency.id}`}
                type='text'
                inputMode='numeric'
                pattern='[0-9]*'
                value={cardNumber}
                onChange={(e) => handleChange(e.target.value)}
                maxLength={maxAllowedLength}
              />
              {showLengthError ? (
                <LengthHint
                  variant='body2'
                  textColor={palette.secondary.purple700}
                >
                  {`Card number must be ${formatAllowedDigitLengths(
                    allowedLengths
                  )} digits`}
                </LengthHint>
              ) : null}
            </FieldGroup>
          </Content>
        </ModalBody>
        <ModalFooter>
          <FooterActions>
            <Button variant='secondary' onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={!canSave} onClick={handleSave}>
              Save
            </Button>
          </FooterActions>
        </ModalFooter>
      </Modal>
    )
  }
)

LinkTransitPassModal.displayName = 'LinkTransitPassModal'

export default LinkTransitPassModal
